/**
 * SignalCard.js
 * =============
 *
 * One card per signal/feature on the Signals overview page
 * (``containers/signal_SignalList.js``).
 *
 * The card surfaces, for a single signal:
 *   - identity            (key name, family chip, value-type chips)
 *   - quality             (patterned, anomalous, high-relevance chips)
 *   - relevance gauge     (from the per-signal relevance API)
 *   - shape stats         (records, distinct value range, time span)
 *   - mini distribution   (sparkline-style bar strip from ``signal.bins``)
 *   - delete affordance   (per-card "Delete" button, parent confirms)
 *
 * Helper exports (also consumed by ``signal_SignalList.js`` for filtering
 * and KPI aggregation):
 *
 *   - ``familyOf(signal)``   classifies the signal into one of:
 *                            'numeric' | 'categorical' | 'binary' | 'time'
 *                            | 'text' | 'id' | 'spatial' | 'mixed'
 *   - ``hasAnomalies(signal)`` returns true if any bin has anomalies
 *
 * Design intent
 * -------------
 * Visually dense but information-dense too. The grid layout in
 * ``SignalList`` expects ~280 px high cards; we keep each section
 * small enough to fit while still giving the user a glance-level
 * read on each signal's health.
 */

import React, { useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { ResponsiveBar } from '@nivo/bar';
import { ResponsiveLine } from '@nivo/line';
import SignalBinMiniChart, { MAX_CHART_POINTS } from '../../containers/charts/signalBinsMini';
import {
  Numbers as NumbersIcon,
  Category as CategoryIcon,
  ToggleOn as ToggleOnIcon,
  Schedule as ScheduleIcon,
  TextFields as TextFieldsIcon,
  Tag as TagIcon,
  Place as PlaceIcon,
  Layers as LayersIcon,
  WaterfallChart as WaterfallChartIcon,
  Warning as WarningIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  StarOutline as StarOutlineIcon,
  DeleteOutline as DeleteOutlineIcon,
  TrendingUp as TrendingUpIcon,
  Storefront as StorefrontIcon,
  AddBusiness as AddBusinessIcon,
  Verified as VerifiedIcon,
  Cancel as CancelIcon,
  Bolt as BoltIcon,
  EditOutlined as EditOutlinedIcon,
} from '@mui/icons-material';

import { formatLargeNumber, formatNumber, secondsToHumanReadable } from '../../utils/utils';
import { useCanOpenPublishFlow } from '../marketplace/marketplaceUtils';

// ---------------------------------------------------------------------------
// Family classification
// ---------------------------------------------------------------------------
//
// We avoid a hard mapping because the same signal can show up with a
// mix of `treat_as` types or just `value_types` (older signals). We
// classify by *priority*: time > spatial > id > binary > numeric >
// categorical > text > mixed, on a best-effort basis.

const TREAT_AS_TIME = new Set([
  'datetime', 'date', 'time', 'timestamp',
]);
const TREAT_AS_NUMERIC = new Set([
  'continuous_numerical', 'discrete_numerical', 'numeric', 'integer', 'float',
]);
const TREAT_AS_CATEGORICAL = new Set([
  'nominal_categorical', 'categorical_text', 'categorical', 'ordinal',
]);
const TREAT_AS_TEXT = new Set([
  'text', 'string', 'free_text',
]);
const TREAT_AS_ID = new Set([
  'identifier', 'id', 'uuid',
]);
const TREAT_AS_SPATIAL = new Set([
  'coordinate', 'geo', 'latitude', 'longitude', 'geohash',
]);
const TREAT_AS_BINARY = new Set([
  'binary', 'boolean', 'bool',
]);

const _has = (xs, set) =>
  Array.isArray(xs) && xs.some((x) => set.has(String(x).toLowerCase()));

/**
 * familyOf(signal) -> one of the family enum strings used for
 * filtering and KPI aggregation in SignalList.
 *
 * Resilient to older signals that only ship `value_types` (no
 * `treat_as`) and to mixed-type rows.
 */
export const familyOf = (signal) => {
  if (!signal || typeof signal !== 'object') return 'mixed';
  // Events (Events Engine) are first-class rows interleaved into the
  // signals list and tagged ``type: 'event'``. They render with a
  // distinct Event family (badge + sentiment/scores, no sparkline).
  if (signal.type === 'event') return 'event';
  const treat = signal.treat_as || [];
  const vts = signal.value_types || [];

  if (_has(treat, TREAT_AS_TIME)) return 'time';
  if (_has(treat, TREAT_AS_SPATIAL)) return 'spatial';
  if (_has(treat, TREAT_AS_ID)) return 'id';
  if (_has(treat, TREAT_AS_BINARY)) return 'binary';

  // Heuristic binary detection from bin structure: only two distinct
  // values across the dataset.
  const distinctBins = (signal.bins || [])
    .map((b) => (b?.value !== undefined ? b.value : b?.label))
    .filter((v) => v !== undefined && v !== null);
  if (distinctBins.length === 2 && _has(treat, TREAT_AS_NUMERIC)) {
    return 'binary';
  }

  if (_has(treat, TREAT_AS_NUMERIC)) return 'numeric';
  if (_has(treat, TREAT_AS_CATEGORICAL)) return 'categorical';
  if (_has(treat, TREAT_AS_TEXT)) return 'text';

  // Fallback to value_types when treat_as is empty.
  if (vts.includes('number') || vts.includes('integer') || vts.includes('float')) {
    return 'numeric';
  }
  if (vts.includes('string')) return 'text';
  if (vts.includes('boolean')) return 'binary';
  if (vts.length > 1) return 'mixed';
  return 'mixed';
};

/**
 * isEventLike(row) -> true when a list row is an Events-Engine event
 * (tagged ``type: 'event'``) rather than a signal time-series. Used by the
 * row renderers to suppress the sparkline and show sentiment/scores instead.
 */
export const isEventLike = (row) => !!row && typeof row === 'object' && row.type === 'event';

/**
 * hasAnomalies(signal) -> true if ANY bin in the signal has at least
 * one anomalous observation. The backend ships `anomalies` on each
 * bin in two shapes:
 *   - bin.anomalies = [{anomalies: [...], lower_bound, upper_bound}]   (list)
 *   - bin.anomalies = [...]                                            (legacy)
 * We tolerate both.
 */
export const hasAnomalies = (signal) => {
  const bins = signal?.bins;
  if (!Array.isArray(bins) || bins.length === 0) return false;
  for (const bin of bins) {
    if (!bin) continue;
    const a = bin.anomalies;
    if (!a) continue;
    if (Array.isArray(a)) {
      if (a.length === 0) continue;
      // Wrapped form: list of {anomalies: [...]}.
      if (typeof a[0] === 'object' && a[0] !== null && 'anomalies' in a[0]) {
        if (a.some((item) => Array.isArray(item.anomalies) && item.anomalies.length > 0)) {
          return true;
        }
        continue;
      }
      // Legacy form: bare array of (i, val) tuples.
      return true;
    }
  }
  return false;
};

// ---------------------------------------------------------------------------
// Family display
// ---------------------------------------------------------------------------

const FAMILY_META = {
  event:       { label: 'Event',       icon: <BoltIcon          fontSize="small" />, tone: 'error' },
  numeric:     { label: 'Numeric',     icon: <NumbersIcon       fontSize="small" />, tone: 'primary' },
  categorical: { label: 'Categorical', icon: <CategoryIcon      fontSize="small" />, tone: 'secondary' },
  binary:      { label: 'Binary',      icon: <ToggleOnIcon      fontSize="small" />, tone: 'info' },
  time:        { label: 'Time',        icon: <ScheduleIcon      fontSize="small" />, tone: 'success' },
  text:        { label: 'Text',        icon: <TextFieldsIcon    fontSize="small" />, tone: 'warning' },
  id:          { label: 'Identifier',  icon: <TagIcon           fontSize="small" />, tone: 'neutral' },
  spatial:     { label: 'Spatial',     icon: <PlaceIcon         fontSize="small" />, tone: 'success' },
  mixed:       { label: 'Mixed',       icon: <LayersIcon        fontSize="small" />, tone: 'neutral' },
};

const toneToColor = (theme, tone) => {
  switch (tone) {
    case 'primary':   return theme.palette.primary.main;
    case 'secondary': return theme.palette.secondary.main;
    case 'success':   return theme.palette.success.main;
    case 'info':      return theme.palette.info.main;
    case 'warning':   return theme.palette.warning.main;
    case 'error':     return theme.palette.error.main;
    case 'neutral':
    default:          return theme.palette.text.secondary;
  }
};

// ---------------------------------------------------------------------------
// Stats derivation
// ---------------------------------------------------------------------------
//
// All stats below are derived in a single O(bins) pass from the
// already-fetched ``signal.bins`` (numeric bins ship min/max/avg/std/
// p10/p90/median/firstValue/lastValue/count per bin). NO additional
// network calls. For workspaces with hundreds of features this adds
// negligible render time vs the chart itself.

const _formatCadence = (sec) => {
  if (!sec || !Number.isFinite(sec) || sec <= 0) return null;
  if (sec < 90) return 'every minute';
  if (sec < 60 * 90) return 'minute';
  if (sec < 3600 * 36) return 'hourly';
  if (sec < 86400 * 10) return 'daily';
  if (sec < 86400 * 45) return 'weekly';
  if (sec < 86400 * 400) return 'monthly';
  return 'yearly';
};

const _humanizeAge = (sec) => {
  if (!Number.isFinite(sec) || sec < 0) return null;
  if (sec < 60) return 'just now';
  if (sec < 3600) return `${Math.round(sec / 60)} min ago`;
  if (sec < 86400) return `${Math.round(sec / 3600)} h ago`;
  if (sec < 86400 * 30) return `${Math.round(sec / 86400)} d ago`;
  if (sec < 86400 * 365) return `${Math.round(sec / (86400 * 30))} mo ago`;
  return `${Math.round(sec / (86400 * 365))} y ago`;
};

const _formatStat = (v, decimals = 2) => {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—';
  if (Math.abs(v) >= 1000) return formatLargeNumber(v) || formatNumber(v);
  return Number(v).toLocaleString(undefined, {
    maximumFractionDigits: decimals,
    minimumFractionDigits: 0,
  });
};

// Map seasonality.lags onto a human period label. We accept either
// the old shape ({primary_season: <int>}) or the new compact shape
// ({lags: {<lag>: <strength>}}).
const _seasonalityPeriodLabel = (seasonality) => {
  if (!seasonality) return null;
  const period = seasonality.primary_season
    ?? (seasonality.lags
      ? Number(Object.keys(seasonality.lags).sort(
          (a, b) => seasonality.lags[b] - seasonality.lags[a],
        )[0])
      : null);
  if (!Number.isFinite(period) || period <= 0) return null;
  if (period <= 1)  return 'cyclic';
  if (period <= 12) return 'short cycle';
  if (period <= 24 + 4) return 'daily';
  if (period <= 7 * 24 + 12) return 'weekly';
  if (period <= 30 + 6) return 'monthly';
  if (period <= 92) return 'quarterly';
  if (period <= 366 + 30) return 'yearly';
  return `period ${period}`;
};

const _countAnomalies = (bin) => {
  const a = bin?.anomalies;
  if (!Array.isArray(a) || a.length === 0) return 0;
  if (typeof a[0] === 'object' && a[0] !== null && 'anomalies' in a[0]) {
    return a.reduce(
      (acc, item) => acc + (Array.isArray(item.anomalies) ? item.anomalies.length : 0),
      0,
    );
  }
  return a.length;
};

/**
 * Derive a compact stats object from signal + per-key priors.
 *
 * Pure function, no hooks, returns only primitives + small arrays.
 * Safe to call inside a useMemo with [signal, priors] deps.
 */
const computeSignalStats = (signal, priors) => {
  const bins = Array.isArray(signal?.bins) ? signal.bins : [];
  const isNumeric = bins.some((b) => b && (b.from !== undefined || b.avgValue !== undefined));

  // ------------- numeric path (time-bucketed bins) -------------
  if (isNumeric) {
    let totalCount = 0;
    let weightedAvg = 0;
    let weightedVar = 0;
    let minV = Infinity;
    let maxV = -Infinity;
    let medianAccum = 0;
    let medianCount = 0;
    let p10Accum = 0;
    let p90Accum = 0;
    let pCount = 0;
    let outlierCount = 0;
    let frozenBins = 0;
    let nonZeroBins = 0;
    let firstValue = null;
    let lastValue = null;
    let lastTs = null;

    // For trend: linear regression on bin avgValue vs index.
    let sumI = 0, sumA = 0, sumII = 0, sumIA = 0, regN = 0;

    bins.forEach((b, i) => {
      if (!b) return;
      const c = Number(b.count) || 0;
      const a = Number(b.avgValue);
      const s = Number(b.stdDev);
      const mn = Number(b.minValue);
      const mx = Number(b.maxValue);
      const md = Number(b.medianValue);
      const p10 = Number(b.p10);
      const p90 = Number(b.p90);

      if (c > 0) nonZeroBins += 1;
      totalCount += c;

      if (Number.isFinite(a) && c > 0) {
        weightedAvg += a * c;
        // Approximate within-bin variance contribution.
        if (Number.isFinite(s)) weightedVar += (s * s) * c;
        sumI += i; sumA += a; sumII += i * i; sumIA += i * a;
        regN += 1;
      }
      if (Number.isFinite(mn)) minV = Math.min(minV, mn);
      if (Number.isFinite(mx)) maxV = Math.max(maxV, mx);
      if (Number.isFinite(md) && c > 0) { medianAccum += md * c; medianCount += c; }
      if (Number.isFinite(p10) && c > 0) { p10Accum += p10 * c; pCount += c; }
      if (Number.isFinite(p90) && c > 0) { p90Accum += p90 * c; }
      if (Number.isFinite(s) && s === 0 && c > 0) frozenBins += 1;
      outlierCount += _countAnomalies(b);

      if (firstValue === null && Number.isFinite(Number(b.firstValue)) && c > 0) {
        firstValue = Number(b.firstValue);
      }
      if (Number.isFinite(Number(b.lastValue)) && c > 0) {
        lastValue = Number(b.lastValue);
        lastTs = b.to ?? b.from ?? lastTs;
      }
    });

    const mean = totalCount > 0 ? weightedAvg / totalCount : null;
    const std = totalCount > 0
      ? Math.sqrt(Math.max(0, weightedVar / totalCount))
      : null;
    const median = medianCount > 0 ? medianAccum / medianCount : null;
    const p10 = pCount > 0 ? p10Accum / pCount : null;
    const p90 = pCount > 0 ? p90Accum / pCount : null;
    const range = (Number.isFinite(minV) && Number.isFinite(maxV))
      ? (maxV - minV) : null;

    // Skew direction. Heuristic: compare (p90-median) vs (median-p10).
    // Falls back to (mean vs median) when percentiles aren't available.
    let skew = 'symmetric';
    if (Number.isFinite(p10) && Number.isFinite(p90) && Number.isFinite(median)) {
      const u = p90 - median; const l = median - p10;
      const span = p90 - p10;
      if (span > 0) {
        const ratio = (u - l) / span;
        if (ratio > 0.25) skew = 'right';
        else if (ratio < -0.25) skew = 'left';
        else if ((u + l) > 0 && Math.max(u, l) / (u + l) > 0.85) skew = 'heavy_tail';
      }
    } else if (Number.isFinite(mean) && Number.isFinite(median)) {
      const denom = std || (range ? range / 6 : 1);
      const z = denom > 0 ? (mean - median) / denom : 0;
      if (z > 0.3) skew = 'right';
      else if (z < -0.3) skew = 'left';
    }

    // Trend slope from linear regression on bin avgValue vs index.
    // Slope is converted to "% change over span" so it's display-friendly.
    let trendDirection = 'flat';
    let trendPctOverSpan = null;
    if (regN >= 3) {
      const meanI = sumI / regN;
      const meanA = sumA / regN;
      const denom = sumII - regN * meanI * meanI;
      if (denom > 0) {
        const slope = (sumIA - regN * meanI * meanA) / denom;
        const totalChange = slope * (regN - 1);
        if (Number.isFinite(mean) && Math.abs(mean) > 1e-9) {
          trendPctOverSpan = (totalChange / Math.abs(mean)) * 100;
          if (trendPctOverSpan > 5) trendDirection = 'up';
          else if (trendPctOverSpan < -5) trendDirection = 'down';
        }
      }
    }

    // Staleness
    let stalenessSec = null;
    const lastTsMs = lastTs ? new Date(lastTs).getTime()
      : (signal?.max_timestamp ? new Date(signal.max_timestamp).getTime() : null);
    if (lastTsMs) {
      stalenessSec = Math.max(0, (Date.now() - lastTsMs) / 1000);
    }

    // Cadence (avg seconds between records)
    const dur = Number(signal?.total_duration);
    const cadenceSec = (Number.isFinite(dur) && dur > 0 && totalCount > 1)
      ? dur / Math.max(1, totalCount - 1) : null;

    // Completeness from bins (fraction of bins with at least one record).
    const completeness = bins.length > 0 ? nonZeroBins / bins.length : null;

    // Stationarity flag — heuristic blend of drift score + trend.
    // We treat stationarity as "no large drift AND no strong trend".
    const driftEntry = (priors?.drift?.per_key || []).find((d) => d.key === signal?.key);
    const driftScore = driftEntry ? Number(driftEntry.drift_score) : null;
    let stationarity = 'unknown';
    if (driftScore !== null) {
      const trendStrong = trendPctOverSpan !== null && Math.abs(trendPctOverSpan) > 30;
      const driftStrong = driftScore > 0.5;
      if (driftStrong || trendStrong) stationarity = 'non_stationary';
      else if (driftScore > 0.2 || (trendPctOverSpan !== null && Math.abs(trendPctOverSpan) > 10)) {
        stationarity = 'needs_differencing';
      } else {
        stationarity = 'stationary';
      }
    }

    // Recommended transform (heuristic).
    let recommendedTransform = 'none';
    const hasNeg = Number.isFinite(minV) && minV < 0;
    if (skew === 'right' && !hasNeg && Number.isFinite(minV) && minV >= 0) {
      recommendedTransform = 'log1p';
    } else if (skew === 'left' && !hasNeg) {
      recommendedTransform = 'sqrt';
    } else if (skew === 'heavy_tail') {
      recommendedTransform = 'clip_outliers';
    } else if (Number.isFinite(std) && Number.isFinite(mean)
      && Math.abs(mean) > 1e-9 && std / Math.abs(mean) > 5) {
      recommendedTransform = 'zscore';
    }

    // Autocorrelation summary from priors (if precomputed).
    const acEntry = (priors?.autocorrelation?.per_key || []).find(
      (d) => d.key === signal?.key,
    );

    // Seasonality period label.
    const seasonalityPeriod = _seasonalityPeriodLabel(signal?.seasonality);

    return {
      kind: 'numeric',
      mean,
      std,
      median,
      p10,
      p90,
      min: Number.isFinite(minV) ? minV : null,
      max: Number.isFinite(maxV) ? maxV : null,
      range,
      firstValue,
      lastValue,
      stalenessSec,
      stalenessLabel: _humanizeAge(stalenessSec),
      cadenceSec,
      cadenceLabel: _formatCadence(cadenceSec),
      completeness,
      outlierCount,
      outlierPct: totalCount > 0 ? outlierCount / totalCount : null,
      frozenBins,
      skew,
      trendDirection,
      trendPctOverSpan,
      stationarity,
      driftScore,
      seasonalityPeriod,
      autocorr: acEntry ? {
        lag_1: Number.isFinite(acEntry.lag_1) ? acEntry.lag_1 : null,
        lag_7: Number.isFinite(acEntry.lag_7) ? acEntry.lag_7 : null,
        lag_30: Number.isFinite(acEntry.lag_30) ? acEntry.lag_30 : null,
      } : null,
      recommendedTransform,
    };
  }

  // ------------- categorical path (top-N value frequencies) -------------
  let totalCount = 0;
  bins.forEach((b) => { totalCount += Number(b?.count) || 0; });
  const sorted = [...bins].sort((a, b) => (b?.count || 0) - (a?.count || 0));
  const top = sorted.slice(0, 3).map((b) => ({
    value: b?._id ?? b?.value ?? '',
    count: Number(b?.count) || 0,
    pct: totalCount > 0 ? (Number(b?.count) || 0) / totalCount : 0,
  }));
  // Cardinality fallback for categorical: number of distinct bins seen.
  const cardinality = bins.length;

  const driftEntry = (priors?.drift?.per_key || []).find((d) => d.key === signal?.key);
  const driftScore = driftEntry ? Number(driftEntry.drift_score) : null;

  return {
    kind: 'categorical',
    cardinality,
    top,
    totalCount,
    driftScore,
    seasonalityPeriod: _seasonalityPeriodLabel(signal?.seasonality),
    skew: null,
    trendDirection: null,
    stationarity: null,
    autocorr: null,
    recommendedTransform: cardinality > 12 ? 'target_encoding'
      : cardinality > 1 ? 'one_hot' : 'none',
  };
};

// ---------------------------------------------------------------------------
// Sub-renderers
// ---------------------------------------------------------------------------

const Gauge = ({ label, value, color, loading }) => {
  const v = typeof value === 'number' && Number.isFinite(value)
    ? Math.max(0, Math.min(1, value))
    : null;
  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
      }}
    >
      <Typography
        variant="caption"
        color="text.primary"
        sx={{
          fontWeight: 700,
          fontSize: 10,
          letterSpacing: 0.3,
          width: 70,
          flexShrink: 0,
        }}
      >
        {label}
      </Typography>
      <LinearProgress
        variant={loading ? 'indeterminate' : 'determinate'}
        value={v === null ? 0 : v * 100}
        sx={{
          flex: 1,
          height: 5,
          borderRadius: 3,
          backgroundColor: alpha(color, 0.1),
          '& .MuiLinearProgress-bar': { backgroundColor: color },
        }}
      />
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          fontVariantNumeric: 'tabular-nums',
          color,
          width: 32,
          textAlign: 'right',
          flexShrink: 0,
        }}
      >
        {loading ? '…' : v === null ? '—' : `${Math.round(v * 100)}%`}
      </Typography>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Data-density strip
// ---------------------------------------------------------------------------
//
// One narrow vertical bar per bin, height proportional to the bin's
// record count. Answers the question "how much data do I have over
// time?" - distinct from the value chart below it (which answers
// "what are the values doing?"). Anomalous bins are coloured red so
// gaps and spikes in volume are obvious at a glance.

const DataDensityStrip = ({ bins }) => {
  const theme = useTheme();

  const data = useMemo(() => {
    if (!Array.isArray(bins) || bins.length === 0) return [];
    // Mirror the line chart's most-recent window so each density bar
    // lines up with its bin above.
    const windowed = bins.length > MAX_CHART_POINTS
      ? bins.slice(-MAX_CHART_POINTS)
      : bins;
    const counts = windowed.map((b) =>
      typeof b?.count === 'number' ? b.count
        : typeof b?.frequency === 'number' ? b.frequency
        : 0,
    );
    const max = Math.max(1, ...counts);
    return windowed.map((b, i) => ({
      h: counts[i] / max,
      count: counts[i],
      from: b?.from || null,
      to: b?.to || null,
      raw: b?._id ?? null,
      anomaly: _binIsAnomalous(b),
    }));
  }, [bins]);

  if (data.length === 0) return null;

  const baseColor = theme.palette.primary.main;

  return (
    <Box
      sx={{
        // Sit flush against the chart above - the strip's purpose
        // (per-bin volume) is obvious from its alignment with the
        // chart's bins, no caption needed.
        //
        // Horizontal insets match the line chart's plot area so each
        // density bar lines up with the corresponding bin on the
        // chart above:
        //   * line chart margin = { left: 44, right: 10 } in
        //     ``signalBinsMini.js``
        // If you ever change those, update this padding too.
        mt: -0.5,
        pl: '44px',
        pr: '10px',
        height: 28,
        display: 'flex',
        alignItems: 'flex-end',
        gap: '2px',
      }}
    >
        {data.map((d, i) => (
          <Tooltip
            key={i}
            title={
              <Box sx={{ fontSize: 11 }}>
                {d.from && (
                  <Box>
                    {new Date(d.from).toLocaleDateString(undefined, {
                      month: 'short', day: '2-digit', year: 'numeric',
                    })}
                    {d.to && d.to !== d.from && (
                      <>
                        {' → '}
                        {new Date(d.to).toLocaleDateString(undefined, {
                          month: 'short', day: '2-digit',
                        })}
                      </>
                    )}
                  </Box>
                )}
                {d.raw && <Box>{String(d.raw)}</Box>}
                <Box sx={{ fontWeight: 700, mt: 0.25 }}>
                  {formatNumber(d.count)} records
                </Box>
                {d.anomaly && (
                  <Box sx={{ color: theme.palette.error.light, mt: 0.25 }}>
                    ⚠ anomalous
                  </Box>
                )}
              </Box>
            }
          >
            <Box
              sx={{
                flex: 1,
                minWidth: 2,
                height: `${Math.max(8, d.h * 100)}%`,
                backgroundColor: d.anomaly
                  ? theme.palette.error.main
                  : alpha(baseColor, 0.4 + d.h * 0.5),
                borderRadius: '1px',
                cursor: 'default',
                transition: 'background-color 120ms ease',
                '&:hover': {
                  backgroundColor: d.anomaly
                    ? theme.palette.error.dark
                    : baseColor,
                },
              }}
            />
          </Tooltip>
        ))}
    </Box>
  );
};


// ---------------------------------------------------------------------------
// Distribution chart
// ---------------------------------------------------------------------------
//
// Two bin shapes come back from the backend:
//
//   1. NUMERIC time bins (numeric / time / binary families):
//        { from, to, count, avgValue, minValue, maxValue,
//          medianValue, p10, p90, stdDev, anomalies }
//      -> rendered with the existing ``SignalBinMiniChart`` (line chart
//         with min/max area band + avg line + portal tooltip showing
//         per-bin stats and anomalies). This is the original "rich"
//         chart the page was designed around.
//
//   2. CATEGORICAL value frequencies (categorical / text):
//        { _id, count }
//      -> rendered as a top-N frequency histogram via ``@nivo/bar``
//         (no min/max/avg makes sense, only the count distribution
//         across distinct values).
//
// In both cases anomalous bins (when present) get the error tone so
// the chart calls them out at a glance.

const _binIsAnomalous = (bin) => {
  const a = bin?.anomalies;
  if (!Array.isArray(a) || a.length === 0) return false;
  if (typeof a[0] === 'object' && a[0] !== null && 'anomalies' in a[0]) {
    return a.some(
      (it) => Array.isArray(it.anomalies) && it.anomalies.length > 0,
    );
  }
  return true;
};

const _isTimeBin = (bin) =>
  bin && (bin.from !== undefined || bin.to !== undefined);

const _formatBinLabel = (bin, kind, i) => {
  if (kind === 'numeric') {
    const ts = bin.from;
    if (!ts) return String(i);
    try {
      const d = new Date(ts);
      if (Number.isNaN(d.getTime())) return String(i);
      return d.toLocaleDateString(undefined, {
        month: 'short', day: '2-digit',
      });
    } catch (_e) {
      return String(i);
    }
  }
  // categorical
  const v = bin._id ?? bin.value ?? bin.label ?? String(i);
  return String(v).length > 14 ? `${String(v).slice(0, 13)}…` : String(v);
};

const _formatNumber = (v) => {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  if (Math.abs(v) >= 1000) return formatLargeNumber(v) || formatNumber(v);
  return Number(v).toLocaleString(undefined, {
    maximumFractionDigits: 2,
  });
};

const CategoricalBinChart = ({ bins }) => {
  const theme = useTheme();

  const data = useMemo(() => {
    const sorted = [...bins].sort(
      (a, b) => (b?.count || 0) - (a?.count || 0),
    );
    return sorted.slice(0, 16).map((b, i) => ({
      idx: i,
      x: _formatBinLabel(b, 'categorical', i),
      raw: b?._id ?? b?.value ?? '',
      count: b?.count ?? 0,
      anomaly: _binIsAnomalous(b),
    }));
  }, [bins]);

  const nivoTheme = useMemo(() => ({
    background: 'transparent',
    text: { fill: theme.palette.text.secondary, fontSize: 9 },
    grid: {
      line: {
        stroke: alpha(theme.palette.divider, 0.5),
        strokeDasharray: '2 4',
      },
    },
    axis: {
      domain: { line: { stroke: 'transparent' } },
      ticks: {
        line: { stroke: 'transparent' },
        text: { fill: theme.palette.text.secondary, fontSize: 9 },
      },
    },
    tooltip: {
      container: {
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 6,
        boxShadow: theme.shadows[3],
        fontSize: 11,
        padding: 0,
      },
    },
  }), [theme]);

  const tooltip = ({ data: d, value }) => (
    <Box sx={{ px: 1.5, py: 1, minWidth: 140 }}>
      <Typography
        variant="caption"
        sx={{ color: theme.palette.text.secondary, display: 'block' }}
      >
        {d.raw || d.x}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.25 }}>
        {_formatNumber(value)} records
      </Typography>
      {d.anomaly && (
        <Box
          sx={{
            mt: 0.5,
            fontSize: 11,
            color: theme.palette.error.main,
            fontWeight: 600,
          }}
        >
          ⚠ Anomalous bin
        </Box>
      )}
    </Box>
  );

  const tickStep = Math.max(1, Math.ceil(data.length / 6));
  const tickValues = data
    .filter((_, i) => i % tickStep === 0)
    .map((d) => d.x);

  return (
    <Box sx={{ height: 63, mt: 0.5 }}>
      <ResponsiveBar
        data={data}
        keys={['count']}
        indexBy="x"
        margin={{ top: 4, right: 4, bottom: 18, left: 28 }}
        padding={0.22}
        borderRadius={1.5}
        enableLabel={false}
        colors={({ data: d }) =>
          d.anomaly
            ? theme.palette.error.main
            : theme.palette.primary.main}
        axisBottom={{
          tickSize: 0,
          tickPadding: 4,
          tickValues,
          format: (v) => v,
        }}
        axisLeft={{
          tickSize: 0,
          tickPadding: 4,
          tickValues: 3,
          format: (v) => _formatNumber(v),
        }}
        gridYValues={3}
        enableGridY
        theme={nivoTheme}
        tooltip={tooltip}
        animate={false}
      />
    </Box>
  );
};


/**
 * Top-level bin chart router.
 *
 * Routes numeric/time-bucketed bins to ``SignalBinMiniChart`` (the
 * original min/max-area + avg-line visualization the page was designed
 * around), and categorical/value-frequency bins to
 * ``CategoricalBinChart`` (a top-N count histogram via @nivo/bar).
 *
 * Returns a small empty stub when there are no bins at all.
 */
const BinChart = ({ bins, eventMarkers, unit }) => {
  const theme = useTheme();

  if (!Array.isArray(bins) || bins.length === 0) {
    return (
      <Box
        sx={{
          height: 63,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.disabled',
          fontSize: 11,
          background: alpha(theme.palette.text.primary, 0.02),
          borderRadius: 1,
        }}
      >
        No distribution data
      </Box>
    );
  }

  // Numeric/time bins ship `from`/`to`/`avgValue` etc. - send them
  // straight to the original SignalBinMiniChart (min/max-band + avg
  // line + portal tooltip with all bin stats including anomalies).
  if (bins.some(_isTimeBin)) {
    return <SignalBinMiniChart data={bins} eventMarkers={eventMarkers} unit={unit} />;
  }

  // Otherwise the bins are { _id, count } categorical buckets - we
  // render those as a top-N count histogram.
  return <CategoricalBinChart bins={bins} />;
};

const _sentimentSign = (sentiment, polarity) => {
  if (sentiment === 'positive') return 1;
  if (sentiment === 'negative') return -1;
  // neutral: fall back to the sign of polarity, else flat on the zero line.
  if (typeof polarity === 'number') return polarity > 0 ? 1 : polarity < 0 ? -1 : 0;
  return 0;
};

/**
 * EventScoreChart — signed scores over time for an event STREAM.
 *
 * Unlike signals (whose chart is a value distribution), an event stream is a
 * sequence of discrete scored events ordered by ``known_at``. We plot one
 * point per event where the MAGNITUDE is the significance (``rank``, 0..100)
 * and the SIGN is the sentiment: positive events go UP, negative DOWN, with a
 * zero centerline. Each point is colored by its sentiment.
 */
const EventScoreChart = ({ points }) => {
  const theme = useTheme();
  const sentimentColor = (s) => (
    s === 'positive' ? theme.palette.success.main
      : s === 'negative' ? theme.palette.error.main
        : theme.palette.text.secondary
  );

  const series = (points || [])
    .map((p) => {
      const rank = Number(p?.rank) || 0;
      const sign = _sentimentSign(p?.sentiment, p?.polarity);
      // Plot by OCCURRED time (when it happened) — the important clock —
      // falling back to recorded time for older points.
      const t = p?.occurred_at ?? p?.timestamp;
      return {
        x: t ? new Date(t) : new Date(),
        y: rank * sign,        // signed score: +up (positive), -down (negative)
        rank,
        sentiment: p?.sentiment,
        polarity: p?.polarity,
        occurredAt: t,
      };
    })
    .sort((a, b) => a.x - b.x)
    // Keep only the most recent MAX_CHART_POINTS events so a busy stream
    // stays legible (each event is one dot).
    .slice(-MAX_CHART_POINTS);

  const _fmtDate = (d) => {
    try {
      return new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    } catch (_) { return ''; }
  };
  const _fmtDateTime = (d) => {
    try {
      return new Date(d).toLocaleString(undefined, {
        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
      });
    } catch (_) { return ''; }
  };

  if (series.length === 0) {
    return (
      <Box sx={{
        height: 119, display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'text.disabled', fontSize: 11,
        background: alpha(theme.palette.text.primary, 0.02), borderRadius: 1,
      }}>
        No events yet
      </Box>
    );
  }

  // Sentiment-colored dots drawn over the line (robust across nivo versions).
  const PointsLayer = ({ points: pts }) => (
    <g>
      {pts.map((pt) => (
        <circle
          key={pt.id}
          cx={pt.x}
          cy={pt.y}
          r={5}
          fill={sentimentColor(pt.data?.sentiment)}
          stroke={theme.palette.background.paper}
          strokeWidth={1.5}
        />
      ))}
    </g>
  );

  return (
    <Box sx={{ height: 119 }}>
      <ResponsiveLine
        data={[{ id: 'score', data: series }]}
        margin={{ top: 12, right: 16, bottom: 34, left: 40 }}
        xScale={{ type: 'time', format: 'native' }}
        yScale={{ type: 'linear', min: -100, max: 100 }}
        curve="monotoneX"
        enableArea
        areaBaselineValue={0}
        areaOpacity={0.10}
        enablePoints={false}
        colors={[theme.palette.text.secondary]}
        lineWidth={1.5}
        enableGridX={false}
        gridYValues={[-100, -50, 0, 50, 100]}
        axisBottom={{
          tickValues: Math.min(5, series.length),
          format: (v) => _fmtDate(v),
          tickSize: 0,
          tickPadding: 6,
        }}
        axisLeft={{ tickValues: [-100, -50, 0, 50, 100], legend: 'score', legendOffset: -34, legendPosition: 'middle' }}
        markers={[{
          axis: 'y', value: 0,
          lineStyle: { stroke: theme.palette.divider, strokeWidth: 1, strokeDasharray: '3 3' },
        }]}
        layers={['grid', 'markers', 'axes', 'areas', 'lines', PointsLayer, 'mesh']}
        animate={false}
        useMesh
        tooltip={({ point }) => {
          const d = point?.data || {};
          return (
            <Box sx={{
              px: 1, py: 0.5, bgcolor: 'background.paper',
              border: '1px solid', borderColor: 'divider', borderRadius: 1,
              fontSize: 11, boxShadow: 2,
            }}>
              <Box sx={{ fontWeight: 700 }}>rank {Math.round(d.rank ?? Math.abs(d.y))}</Box>
              {d.sentiment && (
                <Box sx={{ color: sentimentColor(d.sentiment), textTransform: 'capitalize' }}>
                  {d.sentiment}
                </Box>
              )}
              {d.x && (
                <Box sx={{ color: 'text.secondary' }}>{_fmtDateTime(d.x)}</Box>
              )}
            </Box>
          );
        }}
      />
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Main card
// ---------------------------------------------------------------------------

const SignalCard = ({
  signal,
  relevance,
  dataPriors,
  onDelete,
  onPublish,
  onUnsubscribe,
  onRename,           // open the rename (display name) editor for this signal
  publishedListing,   // listing row for this signal_key, when published
  eventMarkers,       // workspace event occurrences to overlay as dots
}) => {
  // All hooks MUST run on every render in the same order, so we
  // tolerate ``signal`` being missing inside the hook bodies and only
  // bail out below them.
  const theme = useTheme();

  // Publish-to-marketplace capability gate: hidden unless the
  // workspace's org is a verified provider (or the user can share
  // org-internally). Already-published signals keep the manage
  // affordance regardless so sellers can always delist.
  const canOpenPublishFlow = useCanOpenPublishFlow();

  // Heavy lifting once per (signal, priors) change. O(bins) work.
  const stats = useMemo(
    () => computeSignalStats(signal, dataPriors),
    [signal, dataPriors],
  );

  if (!signal) return null;

  const family = familyOf(signal);
  const isEvent = family === 'event';
  const meta = FAMILY_META[family] || FAMILY_META.mixed;
  const familyColor = toneToColor(theme, meta.tone);
  const anomalous = hasAnomalies(signal);
  const patterned = !!signal.seasonality?.seasonality;

  const relValue = relevance && typeof relevance[signal.key] === 'number'
    ? relevance[signal.key]
    : null;

  const isHighRelevance = typeof relValue === 'number' && relValue >= 0.8;

  // ---- shape stats -------------------------------------------------------
  const recordsLabel = formatLargeNumber(signal.signal_count || 0)
    || formatNumber(signal.signal_count || 0);

  const span = signal.total_duration > 0
    ? secondsToHumanReadable(signal.total_duration)
    : null;

  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        minHeight: 408,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        background: alpha(theme.palette.background.paper, 0.96),
        border: `1px solid ${alpha(theme.palette.divider, 0.18)}`,
        overflow: 'hidden',
      }}
    >
      <CardContent sx={{ flex: 1, p: 1.75, pb: '14px !important', display: 'flex', flexDirection: 'column', gap: 1 }}>
        {/* Marketplace ribbon - subscribed signals get an inline
            badge so the user instantly sees the data is purchased
            (read-only) rather than ingested locally.

            When the seller has deleted/renamed the underlying data
            after publishing (``data_status === 'unavailable'``) we
            recolor the ribbon and surface a "No data from seller"
            line so the buyer knows their active subscription is
            currently un-fulfillable - and can unsubscribe from
            here. */}
        {signal.marketplace && (() => {
          const mktUnavailable
            = signal.marketplace.data_status === 'unavailable';
          const ribbonColor = mktUnavailable
            ? theme.palette.warning.main
            : theme.palette.primary.main;
          return (
            <Box sx={{
              mx: -1.75, mt: -1.75, mb: 0.5,
              px: 1.5, py: 0.5,
              background: alpha(ribbonColor, 0.08),
              borderBottom: `1px solid ${alpha(ribbonColor, 0.18)}`,
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              fontSize: 11,
              color: ribbonColor,
            }}>
              <StorefrontIcon sx={{ fontSize: 14 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>
                  {signal.marketplace.listing_title || 'Marketplace signal'}
                </Box>
                <Box sx={{
                  fontSize: 10,
                  color: 'text.secondary',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>
                  by {signal.marketplace.seller_label || 'Anonymous'}
                  {signal.marketplace.verified && (
                    <Tooltip title="Verified data provider">
                      <VerifiedIcon sx={{
                        fontSize: 11, ml: 0.5, verticalAlign: '-1px',
                        color: theme.palette.primary.main,
                      }} />
                    </Tooltip>
                  )}
                </Box>
              </Box>
              {onUnsubscribe && (
                <Tooltip title={mktUnavailable
                  ? 'No data from seller - unsubscribe'
                  : 'Unsubscribe from this marketplace signal'}>
                  <IconButton
                    size="small"
                    onClick={() => onUnsubscribe(
                      signal.marketplace.subscription_id,
                    )}
                    sx={{ color: ribbonColor }}
                  >
                    <CancelIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          );
        })()}

        {/* top row: name + actions */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={0.25} sx={{ minWidth: 0 }}>
              <Tooltip title={signal.display_name
                ? `${signal.display_name} (key: ${signal.key})`
                : signal.key}>
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  sx={{
                    fontSize: 14,
                    lineHeight: 1.2,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    minWidth: 0,
                  }}
                >
                  {signal.display_name
                    || signal.marketplace?.original_key
                    || signal.key}
                </Typography>
              </Tooltip>
              {onRename && (
                <Tooltip title="Rename — sets the display name used across the platform (charts, segments, trainings)">
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); onRename(signal); }}
                    sx={{
                      p: 0.25,
                      color: 'text.disabled',
                      '&:hover': { color: 'primary.main' },
                    }}
                  >
                    <EditOutlinedIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontSize: 10.5,
                display: 'block',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {[
                (signal.value_types || []).join(', ') || '—',
                signal.unit || null,
                stats?.kind === 'numeric' && stats?.lastValue !== null
                  ? `last ${_formatStat(stats.lastValue, 3)}${signal.unit ? ` ${signal.unit}` : ''}`
                  : null,
                stats?.stalenessLabel,
                stats?.cadenceLabel,
              ].filter(Boolean).join(' · ')}
            </Typography>
          </Box>
          {/* Monetization affordance ($) — colored differently when
              the signal is already published so the user instantly
              sees its status. Marketplace-subscribed signals are
              read-only and don't get this affordance.
              When published, ``onPublish(signal, publishedListing)``
              is the manage-published handler; when not published it's
              the create-listing handler. The parent decides which
              dialog to open based on the second arg. */}
          {!signal.marketplace && onPublish
            && (publishedListing || canOpenPublishFlow) && (
            <Tooltip
              title={publishedListing
                ? `Published · ${publishedListing.subscribers_count || 0} subscriber${(publishedListing.subscribers_count || 0) === 1 ? '' : 's'}`
                : 'Put on Marketplace'}
            >
              <IconButton
                size="small"
                onClick={() => onPublish(signal, publishedListing)}
                sx={{
                  color: publishedListing
                    ? theme.palette.success.main
                    : 'text.secondary',
                  bgcolor: publishedListing
                    ? alpha(theme.palette.success.main, 0.12)
                    : 'transparent',
                  '&:hover': {
                    color: publishedListing
                      ? theme.palette.success.dark
                      : 'primary.main',
                    bgcolor: publishedListing
                      ? alpha(theme.palette.success.main, 0.2)
                      : alpha(theme.palette.primary.main, 0.06),
                  },
                }}
              >
                {publishedListing
                  ? <StorefrontIcon fontSize="small" />
                  : <AddBusinessIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          )}
          {/* Native delete only - marketplace signals are read-only
              and have an Unsubscribe action in the ribbon above. */}
          {!signal.marketplace && onDelete && (
            <Tooltip title="Delete this signal">
              <IconButton
                size="small"
                onClick={() => onDelete(signal.key)}
                sx={{
                  color: 'text.secondary',
                  '&:hover': { color: 'error.main' },
                }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* family + quality chips */}
        <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
          <Chip
            icon={meta.icon}
            label={meta.label}
            size="small"
            sx={{
              height: 22,
              background: alpha(familyColor, 0.1),
              color: familyColor,
              border: `1px solid ${alpha(familyColor, 0.3)}`,
              fontWeight: 600,
              fontSize: 11,
              '& .MuiChip-icon': { color: familyColor },
            }}
          />
          {patterned && (
            <Tooltip
              title={stats?.seasonalityPeriod
                ? `Repeating ${stats.seasonalityPeriod} pattern detected`
                : 'Repeating pattern detected'}
            >
              <Chip
                icon={<WaterfallChartIcon fontSize="small" />}
                label={stats?.seasonalityPeriod
                  ? `Patterned · ${stats.seasonalityPeriod}`
                  : 'Patterned'}
                size="small"
                sx={{
                  height: 22,
                  background: alpha(theme.palette.success.main, 0.1),
                  color: theme.palette.success.main,
                  border: `1px solid ${alpha(theme.palette.success.main, 0.35)}`,
                  fontWeight: 600,
                  fontSize: 11,
                  '& .MuiChip-icon': { color: theme.palette.success.main },
                }}
              />
            </Tooltip>
          )}
          {anomalous && (
            <Tooltip
              title={stats?.outlierCount
                ? `${stats.outlierCount} anomalous record${
                    stats.outlierCount === 1 ? '' : 's'} (${
                    ((stats.outlierPct || 0) * 100).toFixed(2)}%)`
                : 'Anomalies detected'}
            >
              <Chip
                icon={<WarningIcon fontSize="small" />}
                label={stats?.outlierCount
                  ? `Anomalous · ${formatLargeNumber(stats.outlierCount) || stats.outlierCount}`
                  : 'Anomalous'}
                size="small"
                sx={{
                  height: 22,
                  background: alpha(theme.palette.error.main, 0.1),
                  color: theme.palette.error.main,
                  border: `1px solid ${alpha(theme.palette.error.main, 0.35)}`,
                  fontWeight: 600,
                  fontSize: 11,
                  '& .MuiChip-icon': { color: theme.palette.error.main },
                }}
              />
            </Tooltip>
          )}
          {isHighRelevance && (
            <Chip
              icon={<StarOutlineIcon fontSize="small" />}
              label="High relevance"
              size="small"
              sx={{
                height: 22,
                background: alpha(theme.palette.warning.main, 0.1),
                color: theme.palette.warning.main,
                border: `1px solid ${alpha(theme.palette.warning.main, 0.35)}`,
                fontWeight: 600,
                fontSize: 11,
                '& .MuiChip-icon': { color: theme.palette.warning.main },
              }}
            />
          )}
          {/* Drift chip (only when meaningful) */}
          {typeof stats?.driftScore === 'number' && stats.driftScore >= 0.2 && (
            <Tooltip title="Recent values have shifted vs the baseline window. Consider re-training.">
              <Chip
                icon={<TrendingUpIcon fontSize="small" />}
                label={`Drift ${Math.round(stats.driftScore * 100)}%`}
                size="small"
                sx={{
                  height: 22,
                  background: alpha(
                    stats.driftScore >= 0.5
                      ? theme.palette.error.main
                      : theme.palette.warning.main,
                    0.1,
                  ),
                  color: stats.driftScore >= 0.5
                    ? theme.palette.error.main
                    : theme.palette.warning.main,
                  border: `1px solid ${alpha(
                    stats.driftScore >= 0.5
                      ? theme.palette.error.main
                      : theme.palette.warning.main,
                    0.35,
                  )}`,
                  fontWeight: 600,
                  fontSize: 11,
                  '& .MuiChip-icon': {
                    color: stats.driftScore >= 0.5
                      ? theme.palette.error.main
                      : theme.palette.warning.main,
                  },
                }}
              />
            </Tooltip>
          )}
          {/* Skew chip (only when not symmetric) */}
          {stats?.kind === 'numeric' && stats.skew && stats.skew !== 'symmetric' && (
            <Tooltip
              title={
                stats.skew === 'right'    ? 'Right-skewed - consider log transform'
                : stats.skew === 'left'   ? 'Left-skewed - consider sqrt transform'
                : 'Heavy-tailed - consider clipping outliers'
              }
            >
              <Chip
                label={
                  stats.skew === 'right' ? 'Skew ↗'
                  : stats.skew === 'left' ? 'Skew ↘'
                  : 'Heavy tail'
                }
                size="small"
                variant="outlined"
                sx={{
                  height: 22,
                  color: theme.palette.text.secondary,
                  fontWeight: 600,
                  fontSize: 11,
                  borderColor: alpha(theme.palette.text.secondary, 0.4),
                }}
              />
            </Tooltip>
          )}
          {/* Trend chip (only when significant) */}
          {stats?.trendDirection && stats.trendDirection !== 'flat'
            && stats.trendPctOverSpan !== null && (
            <Tooltip title="Mean trends over the captured window">
              <Chip
                label={
                  `Trend ${stats.trendDirection === 'up' ? '↗' : '↘'} ${
                    stats.trendPctOverSpan > 0 ? '+' : ''
                  }${Math.round(stats.trendPctOverSpan)}%`
                }
                size="small"
                variant="outlined"
                sx={{
                  height: 22,
                  color: stats.trendDirection === 'up'
                    ? theme.palette.success.main
                    : theme.palette.error.main,
                  fontWeight: 600,
                  fontSize: 11,
                  borderColor: alpha(
                    stats.trendDirection === 'up'
                      ? theme.palette.success.main
                      : theme.palette.error.main,
                    0.4,
                  ),
                }}
              />
            </Tooltip>
          )}
          {/* Stationarity chip - only when non-stationary */}
          {stats?.stationarity && stats.stationarity === 'non_stationary' && (
            <Tooltip title="Mean and/or variance are unstable; differencing recommended before modeling">
              <Chip
                label="Non-stationary"
                size="small"
                variant="outlined"
                sx={{
                  height: 22,
                  color: theme.palette.warning.main,
                  fontWeight: 600,
                  fontSize: 11,
                  borderColor: alpha(theme.palette.warning.main, 0.5),
                }}
              />
            </Tooltip>
          )}
          {/* Stable fallback - only when nothing else fired */}
          {!patterned && !anomalous && !isHighRelevance
            && (typeof stats?.driftScore !== 'number' || stats.driftScore < 0.2)
            && (!stats?.skew || stats.skew === 'symmetric')
            && (!stats?.trendDirection || stats.trendDirection === 'flat') && (
            <Chip
              icon={<CheckCircleOutlineIcon fontSize="small" />}
              label="Stable"
              size="small"
              variant="outlined"
              sx={{
                height: 22,
                color: 'text.secondary',
                fontWeight: 500,
                fontSize: 11,
              }}
            />
          )}
        </Stack>

        {/* shape stats: 3-col when completeness is computable, 2-col otherwise */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: stats?.completeness !== null
              && stats?.completeness !== undefined
              ? '1fr 1fr 1fr' : '1fr 1fr',
            gap: 1,
            mt: 0.5,
          }}
        >
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: 10, fontWeight: 600 }}
            >
              RECORDS
            </Typography>
            <Typography
              fontWeight={700}
              sx={{ fontSize: 14, fontVariantNumeric: 'tabular-nums' }}
            >
              {recordsLabel}
            </Typography>
          </Box>
          {stats?.completeness !== null && stats?.completeness !== undefined && (
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: 10, fontWeight: 600 }}
              >
                COMPLETE
              </Typography>
              <Typography
                fontWeight={700}
                sx={{
                  fontSize: 14,
                  fontVariantNumeric: 'tabular-nums',
                  color: stats.completeness >= 0.95 ? theme.palette.success.main
                    : stats.completeness >= 0.7 ? theme.palette.warning.main
                    : theme.palette.error.main,
                }}
              >
                {`${Math.round(stats.completeness * 100)}%`}
              </Typography>
            </Box>
          )}
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: 10, fontWeight: 600 }}
            >
              SPAN
            </Typography>
            <Typography
              fontWeight={700}
              sx={{ fontSize: 13, fontVariantNumeric: 'tabular-nums' }}
            >
              {span || '—'}
            </Typography>
          </Box>
        </Box>

        {/* Chart. Events are a sequence of scored occurrences over time, so we
            plot score-per-event (EventScoreChart). Signals show their value
            distribution (numeric: time-bucketed avg; categorical: top-N
            frequency). Always shows VALUES. */}
        {isEvent
          ? <EventScoreChart points={signal.points} />
          : <BinChart bins={signal.bins} eventMarkers={eventMarkers} unit={signal.unit} />}

        {/* data-density strip: one box per bin, height ∝ record count.
            Only meaningful for numeric/time-bucketed bins (not events). */}
        {!isEvent && stats?.kind === 'numeric' && (
          <DataDensityStrip bins={signal.bins} />
        )}

        {/* Event stats strip: count · avg rank · latest sentiment. */}
        {isEvent && Array.isArray(signal.points) && signal.points.length > 0 && (() => {
          const ranks = signal.points
            .map((p) => Number(p?.rank))
            .filter((v) => Number.isFinite(v));
          const avg = ranks.length
            ? Math.round(ranks.reduce((a, b) => a + b, 0) / ranks.length)
            : null;
          const latest = signal.points[signal.points.length - 1] || {};
          const sentimentColor = latest.sentiment === 'positive'
            ? theme.palette.success.main
            : latest.sentiment === 'negative'
              ? theme.palette.error.main
              : theme.palette.text.secondary;
          return (
            <Box sx={{
              mt: 0.25, display: 'flex', alignItems: 'center', flexWrap: 'wrap',
              gap: 0.75, color: 'text.secondary', fontSize: 11,
              fontVariantNumeric: 'tabular-nums',
            }}>
              <Box component="span"><strong>{signal.points.length}</strong> events</Box>
              {avg !== null && (
                <>
                  <Box component="span">·</Box>
                  <Box component="span">avg rank <strong>{avg}</strong></Box>
                </>
              )}
              {latest.sentiment && (
                <>
                  <Box component="span">·</Box>
                  <Box component="span" sx={{ color: sentimentColor, textTransform: 'capitalize' }}>
                    latest {latest.sentiment}
                  </Box>
                </>
              )}
            </Box>
          );
        })()}

        {/* Numeric stats strip: μ · med · σ · range · outliers.
            One concise line that gives a data scientist the gist of
            the distribution without opening the priors. */}
        {!isEvent && stats?.kind === 'numeric' && stats.mean !== null && (
          <Box sx={{
            mt: 0.25,
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 0.75,
            color: 'text.secondary',
            fontSize: 11,
            fontVariantNumeric: 'tabular-nums',
          }}>
            <Box component="span">μ <strong>{_formatStat(stats.mean)}</strong></Box>
            <Box component="span">·</Box>
            <Box component="span">med <strong>{_formatStat(stats.median)}</strong></Box>
            <Box component="span">·</Box>
            <Box component="span">σ <strong>{_formatStat(stats.std)}</strong></Box>
            {Number.isFinite(stats.min) && Number.isFinite(stats.max) && (
              <>
                <Box component="span">·</Box>
                <Box component="span">
                  [{_formatStat(stats.min)} – {_formatStat(stats.max)}]
                </Box>
              </>
            )}
            {signal.unit && (
              <>
                <Box component="span">·</Box>
                <Box component="span" sx={{ fontWeight: 600 }}>{signal.unit}</Box>
              </>
            )}
          </Box>
        )}

        {/* Categorical stats strip: top values inline. */}
        {stats?.kind === 'categorical' && stats.top.length > 0 && (
          <Box sx={{
            mt: 0.25,
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 0.75,
            color: 'text.secondary',
            fontSize: 11,
          }}>
            <Box component="span" sx={{ fontWeight: 600 }}>
              {stats.cardinality} distinct
            </Box>
            <Box component="span">·</Box>
            {stats.top.map((t, i) => (
              <Box key={i} component="span" sx={{ display: 'inline-flex', gap: 0.25 }}>
                <strong>{String(t.value).slice(0, 12) || '—'}</strong>
                <span>{Math.round(t.pct * 100)}%</span>
                {i < stats.top.length - 1 && <Box component="span">·</Box>}
              </Box>
            ))}
          </Box>
        )}

        {/* Autocorrelation lag bars: only when priors precomputed them.
            Tiny inline triplet showing predictability at lag 1 / 7 / 30. */}
        {stats?.autocorr && (
          stats.autocorr.lag_1 !== null
          || stats.autocorr.lag_7 !== null
          || stats.autocorr.lag_30 !== null
        ) && (
          <Tooltip
            title={
              <Box sx={{ fontSize: 11 }}>
                Pearson autocorrelation - higher = more useful as a lag feature
                for that lag distance.
              </Box>
            }
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Typography
                variant="caption"
                sx={{ fontSize: 9, fontWeight: 700, color: 'text.secondary',
                      letterSpacing: 0.4, textTransform: 'uppercase', flexShrink: 0 }}
              >
                LAG
              </Typography>
              {[
                { k: 'lag_1', label: '1' },
                { k: 'lag_7', label: '7' },
                { k: 'lag_30', label: '30' },
              ].map(({ k, label }) => {
                const v = stats.autocorr[k];
                const has = typeof v === 'number';
                const mag = has ? Math.max(0, Math.min(1, Math.abs(v))) : 0;
                const color = mag >= 0.5 ? theme.palette.success.main
                  : mag >= 0.2 ? theme.palette.warning.main
                  : theme.palette.text.disabled;
                return (
                  <Box
                    key={k}
                    sx={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                    }}
                  >
                    <Typography
                      sx={{ fontSize: 9, color: 'text.secondary',
                            fontVariantNumeric: 'tabular-nums', width: 16,
                            flexShrink: 0 }}
                    >
                      {label}
                    </Typography>
                    <Box sx={{
                      flex: 1,
                      height: 4,
                      borderRadius: 2,
                      backgroundColor: alpha(color, 0.15),
                      overflow: 'hidden',
                    }}>
                      <Box sx={{
                        width: `${mag * 100}%`,
                        height: '100%',
                        backgroundColor: color,
                        transition: 'width 200ms ease',
                      }} />
                    </Box>
                    <Typography sx={{
                      fontSize: 9,
                      fontVariantNumeric: 'tabular-nums',
                      color,
                      fontWeight: 700,
                      width: 28,
                      textAlign: 'right',
                      flexShrink: 0,
                    }}>
                      {has ? `${Math.round(mag * 100)}%` : '—'}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Tooltip>
        )}

        {/* gauge - sits naturally below the lag row, no auto-push */}
        <Stack spacing={0.75}>
          <Gauge
            label="Relevance"
            value={relValue}
            color={
              isHighRelevance
                ? theme.palette.warning.main
                : theme.palette.primary.main
            }
          />
        </Stack>
      </CardContent>
    </Card>
  );
};

export default SignalCard;
